import React, { useState, useEffect } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { getHighlyValuedPeople, getRegularValuedPeople, featuredAchievers, regularAchievers } from '@/data/articles';
import { getSuccessStoriesByAchiever } from '@/data/successStories';
import { Award, Star, Calendar, ArrowRight, Users, Briefcase, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ValuedPeopleCard from '@/components/ui/ValuedPeopleCard';
import ValuedPeopleCarousel from '@/components/ui/ValuedPeopleCarousel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Article } from '@/data/articles';

const Achievers: React.FC = () => {
  const highlyValuedPeople = getHighlyValuedPeople();
  const regularValuedPeople = getRegularValuedPeople();
  const allAchievers = [...featuredAchievers, ...regularAchievers, ...highlyValuedPeople, ...regularValuedPeople];
  const [searchParams] = useSearchParams();
  const [selectedAchiever, setSelectedAchiever] = useState<Article | null>(null);
  const navigate = useNavigate();
  
  const achieverId = searchParams.get('id');
  const categoryFilter = searchParams.get('category');
  
  // Filter achievers based on category if provided
  const filteredAchievers = categoryFilter 
    ? allAchievers.filter(achiever => achiever.category === categoryFilter)
    : allAchievers;

  // Set the selected achiever if ID is provided
  useEffect(() => {
    if (achieverId) {
      const achiever = allAchievers.find(a => a.id === achieverId);
      if (achiever) {
        setSelectedAchiever(achiever);
      } else {
        // If achiever not found, clear the selection
        setSelectedAchiever(null);
      }
    } else {
      setSelectedAchiever(null);
    }
  }, [achieverId, allAchievers]);

  // Get success stories for the selected achiever
  const achieverSuccessStories = selectedAchiever 
    ? getSuccessStoriesByAchiever(selectedAchiever.id) 
    : [];

  // Handle view all achievers
  const handleViewAllAchievers = () => {
    navigate('/achievers');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 bg-gradient-to-b from-purple-50/60 to-white">
        {/* Hero Section - Enhanced with more vibrant styling */}
        <div className="relative py-16 bg-gradient-to-r from-purple-100/70 to-amber-100/70 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="container mx-auto px-4 md:px-6 text-center relative z-10">
            <div className="inline-block p-1 bg-amber-100 rounded-full mb-4">
              <Award className="h-7 w-7 text-amber-600" />
            </div>
            {categoryFilter ? (
              <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-800">
                {categoryFilter.charAt(0).toUpperCase() + categoryFilter.slice(1)} Achievers
              </h1>
            ) : selectedAchiever ? (
              <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-800">
                {selectedAchiever.author.name}
              </h1>
            ) : (
              <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-800">Our Achievers</h1>
            )}
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              {selectedAchiever 
                ? selectedAchiever.excerpt 
                : "Celebrating the remarkable individuals who have made significant contributions to their fields and communities through innovation and dedication."}
            </p>
          </div>
        </div>

        {/* Selected Achiever Detail View */}
        {selectedAchiever && (
          <section className="py-16 px-4 md:px-6">
            <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Achiever Profile Card */}
                <div className="lg:col-span-1">
                  <Card className="overflow-hidden border border-purple-100 shadow-md hover:shadow-lg transition-shadow">
                    <div className="relative h-80">
                      <img 
                        src={selectedAchiever.imageUrl} 
                        alt={selectedAchiever.author.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-0 right-0 bg-gradient-to-l from-amber-500 to-amber-400 text-white px-4 py-1.5 text-sm font-medium rounded-bl-xl">
                        <div className="flex items-center">
                          <Star className="h-3.5 w-3.5 mr-1.5" fill="white" />
                          Featured Achiever
                        </div>
                      </div>
                    </div>
                    
                    <CardContent className="p-6">
                      <h2 className="text-2xl font-bold mb-2">{selectedAchiever.author.name}</h2>
                      <p className="text-purple-700 font-medium mb-4">{selectedAchiever.title}</p>
                      
                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <Briefcase className="h-4 w-4 mr-1.5" />
                        <span>{selectedAchiever.category.charAt(0).toUpperCase() + selectedAchiever.category.slice(1)}</span>
                        <span className="mx-2">•</span>
                        <Calendar className="h-4 w-4 mr-1.5" />
                        <span>Recognized {new Date(selectedAchiever.publishedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                      </div>
                      
                      <p className="text-gray-700 mb-6">{selectedAchiever.excerpt}</p>
                      
                      <Button 
                        variant="outline" 
                        className="w-full border-purple-200 hover:bg-purple-50 text-purple-700"
                        onClick={handleViewAllAchievers}
                      >
                        View All Achievers
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Achiever Success Stories */}
                <div className="lg:col-span-2">
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <BookOpen className="h-5 w-5 text-purple-600" />
                      <h2 className="text-2xl font-bold">Success Stories</h2>
                    </div>
                    
                    {achieverSuccessStories.length > 0 ? (
                      <div className="grid grid-cols-1 gap-6">
                        {achieverSuccessStories.map(story => (
                          <Card key={story.id} className="overflow-hidden border border-purple-100 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/success-story/${story.id}`)}>
                            <div className="flex flex-col md:flex-row">
                              <div className="md:w-1/3 h-48">
                                <img 
                                  src={story.image} 
                                  alt={story.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="p-6 md:w-2/3">
                                <h3 className="text-xl font-bold mb-2 text-gray-800 hover:text-purple-700 transition-colors">{story.title}</h3>
                                <p className="text-sm font-medium text-purple-600 mb-3">{story.oneLiner}</p>
                                <p className="text-gray-600 mb-4 line-clamp-2">{story.excerpt}</p>
                                
                                <div className="flex flex-wrap gap-2">
                                  {story.stats.revenue && (
                                    <Badge variant="outline" className="bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200">
                                      {story.stats.revenue}
                                    </Badge>
                                  )}
                                  {story.stats.impact && (
                                    <Badge variant="outline" className="bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200">
                                      {story.stats.impact}
                                    </Badge>
                                  )}
                                  {story.stats.employeeCount && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200">
                                      {story.stats.employeeCount}
                                    </Badge>
                                  )}
                                  {story.stats.partners && (
                                    <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200">
                                      {story.stats.partners}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-8 text-center">
                        <p className="text-gray-500 mb-4">No success stories found for this achiever.</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate('/success-story')}
                        >
                          Browse All Success Stories
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Show all achievers if no specific achiever is selected */}
        {!selectedAchiever && (
          <>
            {/* Featured Achievers - Updated with new custom achievers */}
            <section className="py-16 px-4 md:px-6">
              <div className="container mx-auto">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <Star className="h-6 w-6 text-amber-500" />
                    <h2 className="text-2xl md:text-3xl font-bold">Featured Achievers</h2>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(categoryFilter ? featuredAchievers.filter(a => a.category === categoryFilter) : featuredAchievers).map((achiever) => {
                    // Get success stories count for this achiever
                    const achieverStories = getSuccessStoriesByAchiever(achiever.id);
                    const storiesCount = achieverStories.length;
                    
                    return (
                      <div key={achiever.id} 
                        className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group cursor-pointer"
                        onClick={() => navigate(`/achievers?id=${achiever.id}`)}
                      >
                        <div className="relative">
                          <img 
                            src={achiever.imageUrl} 
                            alt={achiever.author.name} 
                            className="w-full h-80 object-cover object-center transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = '/placeholder.svg';
                            }}
                          />
                          <div className="absolute top-0 right-0 bg-gradient-to-l from-amber-500 to-amber-400 text-white px-4 py-1.5 text-sm font-medium rounded-bl-xl">
                            <div className="flex items-center">
                              <Star className="h-3.5 w-3.5 mr-1.5" fill="white" />
                              Featured Leader
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-6 bg-gradient-to-b from-white to-purple-50/20">
                          <h3 className="text-xl font-bold mb-2 text-gray-800">{achiever.author.name}</h3>
                          <p className="text-purple-700 text-sm font-medium mb-3">{achiever.title}</p>
                          <div className="flex items-center text-sm text-gray-500 mb-4">
                            <Briefcase className="h-4 w-4 mr-1.5" />
                            <span>{achiever.category.charAt(0).toUpperCase() + achiever.category.slice(1)}</span>
                            <span className="mx-2">•</span>
                            <Calendar className="h-4 w-4 mr-1.5" />
                            <span>Recognized {new Date(achiever.publishedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                          </div>
                          <p className="text-gray-700 mb-6 line-clamp-3">{achiever.excerpt}</p>
                          
                          {/* Success story button if they have any */}
                          {storiesCount > 0 && (
                            <Button 
                              variant="outline" 
                              className="w-full text-purple-600 hover:text-purple-800 border border-purple-200 hover:bg-purple-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/achievers?id=${achiever.id}`);
                              }}
                            >
                              <ExternalLink className="w-4 h-4 mr-1.5" />
                              View Story
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default Achievers;
